import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DataTable from "react-data-table-component";

import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import Loader from "../../common/Loader";
import PageHeader from "../PageHeader";
import { triggerAlert } from "../../utils";
import {
    AdminOrganizations,
    AdminPaymentAttempts,
    AdminAgentSubscriptions,
    AdminAgentInvoices,
    AdminPhoneNumbers,
} from "../../utils/APIClient";
import AssignPhoneNumberDialog from "./AssignPhoneNumberDialog";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Customers / View Customer (:orgId)
 *
 * One place for admins to see everything an organization does. Every tab
 * calls a dedicated org-scoped admin endpoint.
 *
 * Read-only apart from the Phone Numbers tab, which can assign a number to
 * this customer and unassign one. That is an admin action by nature: a ported
 * or manually sourced number never appears in the buy-numbers catalogue, so
 * the customer has no way to get it themselves.
 */

const TABS = [
    { key: "overview",       label: "Overview" },
    { key: "subscriptions",  label: "Subscriptions" },
    { key: "invoices",       label: "Invoices" },
    { key: "transactions",   label: "Transactions" },
    { key: "payment_methods", label: "Payment Methods" },
    { key: "activity",       label: "Activity" },
    { key: "coupons",        label: "Coupons" },
    { key: "trials",         label: "Trials" },
    { key: "phone_numbers",  label: "Phone Numbers" },
];

// Human labels + colors for the per-category free-trial outcome pill on
// the Trials tab. Mirrors the backend AdminOrgTrialsView._outcome() enum.
const TRIAL_OUTCOME_META = {
    none:              { label: "Not used",           color: "secondary" },
    active:            { label: "Active",             color: "success"   },
    quota_exhausted:   { label: "Quota exhausted",    color: "warning"   },
    cancelled:         { label: "Cancelled",          color: "secondary" },
    conversion_failed: { label: "Conversion failed",  color: "danger"    },
    converted:         { label: "Converted",          color: "primary"   },
    unknown:           { label: "Unknown",            color: "secondary" },
};

const TRIAL_CATEGORY_META = {
    avatar:     { label: "Avatar Chat",     unit: "units" },
    calling_v2: { label: "Calling",         unit: "minutes" },
    workflow:   { label: "AI Workflow",     unit: "USD" },
};

const CATEGORY_LABELS = {
    avatar: "Avatar",
    calling_v2: "Calling",
    workflow: "Workflow",
    phone_number: "Phone Numbers",
};

const fmtMoney = (n, cur = "USD") => {
    const num = Number(n || 0);
    return `${cur === "USD" ? "$" : cur + " "}${num.toFixed(2)}`;
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso;
    }
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const StatusPill = ({ status }) => {
    const colorMap = {
        succeeded: "success",
        active: "success",
        paid: "success",
        failed: "danger",
        past_due: "danger",
        cancelled: "secondary",
        pending: "warning",
        processing: "info",
        draft: "secondary",
    };
    const c = colorMap[status] || "secondary";
    return (
        <span className={`badge bg-${c} text-uppercase`} style={{ fontSize: "0.7rem" }}>
            {status || "—"}
        </span>
    );
};

const dataTableStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "#064c77",
            borderBottom: "1px solid #dee2e6",
            minHeight: "48px",
        },
    },
    headCells: {
        style: {
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: "700",
            textTransform: "uppercase",
        },
    },
    rows: { style: { minHeight: "52px", backgroundColor: "#ffffff" } },
    cells: {
        style: {
            fontSize: "13px",
            color: "#495057",
            borderBottom: "1px solid #f1f3f5",
        },
    },
};

function CustomerDetail() {
    const { orgId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialTab = searchParams.get("tab") || "overview";
    const [activeTab, setActiveTab] = useState(initialTab);

    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);

    const [tabData, setTabData] = useState({});
    const [tabLoading, setTabLoading] = useState(false);

    const [txnDetail, setTxnDetail] = useState(null); // drawer state for Transactions

    document.title = overview
        ? `Digital Employees - ${overview.organization.name}`
        : "Digital Employees - Customer";

    // ---- overview always loaded so KPI strip renders on any tab ----
    useEffect(() => {
        let cancelled = false;
        setOverviewLoading(true);
        AdminOrganizations.detail(orgId)
            .then((resp) => {
                if (cancelled) return;
                setOverview(resp?.data || null);
            })
            .catch((err) => {
                if (cancelled) return;
                const status = err?.response?.status;
                triggerAlert(
                    "error",
                    "Oops...",
                    status === 404
                        ? "Customer not found."
                        : "Could not load customer detail."
                );
            })
            .finally(() => !cancelled && setOverviewLoading(false));
        return () => { cancelled = true; };
    }, [orgId]);

    // ---- deep-linkable tabs ----
    const handleTabChange = (key) => {
        setActiveTab(key);
        const next = new URLSearchParams(searchParams);
        next.set("tab", key);
        setSearchParams(next, { replace: true });
    };

    // ---- per-tab lazy fetch ----
    const loadTab = useCallback(async (tab) => {
        setTabLoading(true);
        try {
            if (tab === "subscriptions") {
                const r = await AdminAgentSubscriptions.list({ organization: orgId, page_size: 100 });
                setTabData((s) => ({ ...s, subscriptions: r.data?.results || [] }));
            } else if (tab === "invoices") {
                const r = await AdminAgentInvoices.list({ organization: orgId, page_size: 100 });
                setTabData((s) => ({ ...s, invoices: r.data?.results || [] }));
            } else if (tab === "transactions") {
                const r = await AdminPaymentAttempts.list({ organization: orgId, page_size: 100 });
                setTabData((s) => ({ ...s, transactions: r.data?.results || [] }));
            } else if (tab === "payment_methods") {
                const r = await AdminOrganizations.paymentMethods(orgId);
                setTabData((s) => ({ ...s, payment_methods: r.data?.payment_methods || [] }));
            } else if (tab === "activity") {
                const r = await AdminOrganizations.events(orgId, { page_size: 100 });
                setTabData((s) => ({ ...s, activity: r.data?.events || [] }));
            } else if (tab === "coupons") {
                const r = await AdminOrganizations.couponRedemptions(orgId, { page_size: 100 });
                setTabData((s) => ({ ...s, coupons: r.data?.redemptions || [] }));
            } else if (tab === "trials") {
                const r = await AdminOrganizations.trials(orgId);
                setTabData((s) => ({ ...s, trials: r.data?.trials || [] }));
            } else if (tab === "phone_numbers") {
                const r = await AdminOrganizations.phoneNumbers(orgId);
                setTabData((s) => ({ ...s, phone_numbers: r.data?.results || [] }));
            }
        } catch (err) {
            triggerAlert("error", "Oops...", `Could not load ${tab}.`);
        } finally {
            setTabLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        if (activeTab === "overview") return;
        if (tabData[activeTab]) return;
        loadTab(activeTab);
    }, [activeTab, tabData, loadTab]);

    // -------------------------------------------------------------------
    // Column defs
    // -------------------------------------------------------------------
    const subsColumns = useMemo(() => ([
        {
            name: "Plan",
            selector: (r) => r.agent_name || r.agent_plan?.agent_name || "—",
            cell: (r) => r.agent_name || r.agent_plan?.agent_name || "—",
            grow: 2,
        },
        {
            name: "Category",
            selector: (r) => r.category || r.agent_plan?.category,
            cell: (r) => CATEGORY_LABELS[r.category || r.agent_plan?.category] || (r.category || "—"),
            width: "130px",
        },
        {
            name: "Status",
            cell: (r) => <StatusPill status={r.status} />,
            width: "110px",
        },
        {
            name: "Price / cycle",
            cell: (r) => fmtMoney(r.price, r.currency || "USD"),
            width: "130px",
        },
        {
            name: "Started",
            cell: (r) => fmtDate(r.created_at),
            width: "120px",
        },
        {
            name: "Next Renewal",
            cell: (r) => fmtDate(r.next_billing_date || r.current_period_end),
            width: "140px",
        },
    ]), []);

    const invoicesColumns = useMemo(() => ([
        {
            name: "Invoice #",
            selector: (r) => r.invoice_number,
            cell: (r) => (
                <span style={{ fontFamily: "monospace" }}>{r.invoice_number}</span>
            ),
            width: "180px",
        },
        { name: "Plan", cell: (r) => r.agent_name || "—", grow: 2 },
        {
            name: "Total",
            cell: (r) => fmtMoney(r.total, r.currency || "USD"),
            width: "110px",
        },
        {
            name: "Status",
            cell: (r) => <StatusPill status={r.status} />,
            width: "100px",
        },
        {
            name: "Issued",
            cell: (r) => fmtDate(r.created_at),
            width: "120px",
        },
        {
            name: "Actions",
            cell: (r) => (
                <a
                    className="btn btn-sm btn-outline-primary"
                    href={`/api/admin/agent-invoices/${r.id}/pdf/`}
                    target="_blank"
                    rel="noreferrer"
                >
                    PDF
                </a>
            ),
            width: "100px",
        },
    ]), []);

    const txnColumns = useMemo(() => ([
        {
            name: "When",
            cell: (r) => fmtDateTime(r.attempted_at),
            width: "180px",
        },
        {
            name: "Amount",
            cell: (r) => fmtMoney(r.amount, r.currency || "USD"),
            width: "110px",
        },
        {
            name: "Status",
            cell: (r) => <StatusPill status={r.status} />,
            width: "110px",
        },
        {
            name: "Subscription",
            cell: (r) => r.subscription?.agent_plan_name || "—",
            grow: 2,
        },
        {
            name: "Auth.net Txn ID",
            cell: (r) => (
                <span style={{ fontFamily: "monospace", fontSize: "11px" }}>
                    {r.gateway_transaction_id || "—"}
                </span>
            ),
            width: "150px",
        },
        {
            name: "Failure",
            cell: (r) => r.failure_reason || r.failure_message || "—",
            grow: 2,
        },
        {
            name: "",
            cell: (r) => (
                <button
                    className="btn btn-sm btn-link p-0"
                    onClick={() => setTxnDetail(r)}
                >
                    Details
                </button>
            ),
            width: "80px",
        },
    ]), []);

    const activityColumns = useMemo(() => ([
        { name: "When", cell: (r) => fmtDateTime(r.created_at), width: "180px" },
        {
            name: "Event",
            cell: (r) => (
                <span className="badge bg-info text-uppercase">{r.event_type}</span>
            ),
            width: "160px",
        },
        {
            name: "Subscription",
            cell: (r) => r.subscription?.agent_plan_name || "—",
            grow: 2,
        },
        {
            name: "Actor",
            cell: (r) => r.actor_email || "system",
            width: "180px",
        },
    ]), []);

    const [assignOpen, setAssignOpen] = useState(false);
    // Set when reassigning an existing number, so the dialog opens with it
    // filled in rather than making the admin retype a number they can see.
    const [assignPrefill, setAssignPrefill] = useState(null);

    const unassignNumber = async (row) => {
        const ok = await confirm({
            title: "Unassign this number?",
            message:
                `Stop billing and routing for ${row.phone_number_e164}?\n\n` +
                "The number stays on the customer's account — it is never handed back " +
                "to the carrier, because a ported number belongs to them.",
            confirmLabel: "Unassign",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await AdminPhoneNumbers.unassign(row.id);
            triggerAlert("success", "", `${row.phone_number_e164} unassigned.`);
            setTabData((s) => ({ ...s, phone_numbers: null }));
            loadTab("phone_numbers");
        } catch (e) {
            triggerAlert("error", "Oops...",
                e?.response?.data?.error || "Could not unassign the number.");
        }
    };

    const phoneNumbersColumns = useMemo(() => ([
        {
            name: "Number",
            grow: 2,
            cell: (r) => (
                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                    {r.phone_number_e164}
                </span>
            ),
        },
        { name: "Type", cell: (r) => r.line_type || "—", width: "110px" },
        {
            name: "Source",
            width: "120px",
            cell: (r) => {
                if (r.source === "ported") return <span className="badge bg-info">Ported in</span>;
                if (r.source === "assigned") return <span className="badge bg-primary">Assigned</span>;
                return <span className="badge bg-secondary">Bought</span>;
            },
        },
        {
            name: "Monthly",
            width: "110px",
            cell: (r) =>
                r.is_free
                    ? <span className="badge bg-success">Free</span>
                    : fmtMoney(r.monthly_price_usd),
        },
        {
            name: "Billing",
            width: "130px",
            cell: (r) => (
                <div>
                    <div>{r.subscription_status}</div>
                    {!r.has_recurring_billing && r.subscription_status === "active" && (
                        <div className="text-muted small">no auto-renew</div>
                    )}
                </div>
            ),
        },
        { name: "Status", width: "110px", cell: (r) => <StatusPill status={r.status} /> },
        {
            name: "Action",
            width: "140px",
            cell: (r) =>
                r.subscription_status === "active" ? (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => unassignNumber(r)}
                    >
                        Unassign
                    </button>
                ) : (
                    // An unassigned number keeps its row — the customer still
                    // holds the number, it just has no billing. Reassigning
                    // reuses that row, so the dialog is pre-filled with it.
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setAssignPrefill({
                            phone_number_e164: r.phone_number_e164,
                            line_type: r.line_type,
                            source: r.source === "catalogue" ? "assigned" : r.source,
                        })}
                    >
                        Reassign
                    </button>
                ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), []);

    const couponsColumns = useMemo(() => ([
        {
            name: "Code",
            cell: (r) => (
                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                    {r.coupon?.code || "—"}
                </span>
            ),
            width: "140px",
        },
        { name: "Name", cell: (r) => r.coupon?.name || "—", grow: 2 },
        { name: "Type", cell: (r) => r.coupon?.discount_type || "—", width: "120px" },
        {
            name: "Discount",
            cell: (r) => fmtMoney(r.discount_amount),
            width: "100px",
        },
        {
            name: "Original",
            cell: (r) => fmtMoney(r.original_amount),
            width: "100px",
        },
        {
            name: "Subscription",
            cell: (r) => r.subscription?.agent_plan_name || "—",
            grow: 2,
        },
        {
            name: "Redeemed",
            cell: (r) => fmtDateTime(r.redeemed_at),
            width: "180px",
        },
    ]), []);

    // -------------------------------------------------------------------
    // Renderers
    // -------------------------------------------------------------------
    const renderOverview = () => {
        if (!overview) return null;
        const org = overview.organization;
        const pm = overview.primary_member;
        const cats = overview.active_subscriptions_by_category || {};
        return (
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">Organization</h6>
                            <dl className="row mb-0">
                                <dt className="col-sm-4">Name</dt>
                                <dd className="col-sm-8">{org.name}</dd>
                                <dt className="col-sm-4">Plan</dt>
                                <dd className="col-sm-8 text-capitalize">{org.plan}</dd>
                                <dt className="col-sm-4">Billing Email</dt>
                                <dd className="col-sm-8">{org.billing_email || "—"}</dd>
                                <dt className="col-sm-4">Phone</dt>
                                <dd className="col-sm-8">{org.billing_phone || "—"}</dd>
                                <dt className="col-sm-4">Tax ID</dt>
                                <dd className="col-sm-8">{org.tax_id || "—"}</dd>
                                <dt className="col-sm-4">Since</dt>
                                <dd className="col-sm-8">{fmtDate(org.created_at)}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">Billing Address</h6>
                            <div>{org.billing_address_line1 || "—"}</div>
                            {org.billing_address_line2 && <div>{org.billing_address_line2}</div>}
                            <div>
                                {[org.billing_city, org.billing_state, org.billing_pincode]
                                    .filter(Boolean).join(", ") || "—"}
                            </div>
                            <div>{org.billing_country || ""}</div>

                            <h6 className="text-muted mt-4 mb-3">Primary Member</h6>
                            {pm ? (
                                <>
                                    <div><strong>{pm.first_name} {pm.last_name}</strong></div>
                                    <div>{pm.email}</div>
                                    <div className="text-muted">Joined {fmtDate(pm.date_joined)}</div>
                                </>
                            ) : (
                                <div className="text-muted">No members yet.</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">Active Subscriptions by Category</h6>
                            {Object.keys(cats).length === 0 ? (
                                <div className="text-muted">No active subscriptions.</div>
                            ) : (
                                <div className="d-flex flex-wrap gap-2">
                                    {Object.entries(cats).map(([k, v]) => (
                                        <span
                                            key={k}
                                            className="badge bg-primary"
                                            style={{ fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}
                                        >
                                            {CATEGORY_LABELS[k] || k}: {v}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTable = (rows, columns, emptyText) => {
        if (tabLoading) {
            return (
                <div className="text-center p-4">
                    <Loader />
                </div>
            );
        }
        if (!rows || rows.length === 0) {
            return <div className="text-muted p-3">{emptyText}</div>;
        }
        return (
            <DataTable
                columns={columns}
                data={rows}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                fixedHeader
                fixedHeaderScrollHeight="500px"
                persistTableHead
                customStyles={dataTableStyles}
            />
        );
    };

    const renderPaymentMethods = () => {
        if (tabLoading) return <div className="text-center p-4"><Loader /></div>;
        const list = tabData.payment_methods || [];
        if (list.length === 0) {
            return <div className="text-muted p-3">No cards on file.</div>;
        }
        return (
            <div className="row g-3">
                {list.map((pm) => (
                    <div key={pm.id} className="col-md-4">
                        <div className={`card ${pm.is_default ? "border-primary" : ""}`}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start">
                                    <h6 className="mb-0">{pm.card_brand || "Card"}</h6>
                                    {pm.is_default && (
                                        <span className="badge bg-primary">Default</span>
                                    )}
                                </div>
                                <div className="mt-3" style={{ fontFamily: "monospace", fontSize: "1.1rem" }}>
                                    **** **** **** {pm.card_last4 || "----"}
                                </div>
                                <div className="text-muted mt-1">Expires {pm.card_expiry}</div>
                                <div className="text-muted small mt-2">
                                    Added {fmtDate(pm.created_at)}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderTrials = () => {
        if (tabLoading) return <div className="text-center p-4"><Loader /></div>;
        const rows = tabData.trials || [];
        if (!rows.length) {
            return <div className="text-muted p-3">No trial data available.</div>;
        }
        return (
            <div className="row g-3">
                {rows.map((row) => {
                    const catMeta = TRIAL_CATEGORY_META[row.category] || { label: row.category, unit: "" };
                    const outcomeMeta = TRIAL_OUTCOME_META[row.outcome] || TRIAL_OUTCOME_META.unknown;
                    const isNone = row.outcome === "none";
                    return (
                        <div key={row.category} className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <h6 className="mb-0 fw-bold" style={{ color: "#014670" }}>
                                            {catMeta.label}
                                        </h6>
                                        <span className={`badge bg-${outcomeMeta.color}`}>
                                            {outcomeMeta.label}
                                        </span>
                                    </div>
                                    {isNone ? (
                                        <div className="text-muted small">
                                            No free trial has been started in this category.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="small text-muted mb-2">
                                                Plan:{" "}
                                                <span className="text-dark">
                                                    {row.subscription?.agent_plan_name || "—"}
                                                </span>
                                            </div>
                                            <div className="row g-2 small">
                                                <div className="col-6">
                                                    <div className="text-muted">Started</div>
                                                    <div>{fmtDate(row.trial_started_at)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted">Ends</div>
                                                    <div>{fmtDate(row.trial_ends_at)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted">Allowance</div>
                                                    <div>
                                                        {row.allowance == null ? "—" : `${row.allowance} ${catMeta.unit}`}
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted">Used</div>
                                                    <div>
                                                        {row.used == null ? "—" : `${row.used} ${catMeta.unit}`}
                                                    </div>
                                                </div>
                                                {row.cancelled_at && (
                                                    <div className="col-12">
                                                        <div className="text-muted">Cancelled at</div>
                                                        <div>{fmtDateTime(row.cancelled_at)}</div>
                                                    </div>
                                                )}
                                                {row.converted_to_active && row.subscription?.id && (
                                                    <div className="col-12 mt-2">
                                                        <span className="badge bg-primary-subtle text-primary">
                                                            <i className="bi bi-arrow-right-circle me-1" />
                                                            Converted to paid — subscription #{row.subscription.id}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // -------------------------------------------------------------------
    // Frame
    // -------------------------------------------------------------------
    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content">
                    <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                        <div className="container-fluid">
                            <PageHeader
                                mainHeading="Customer Detail"
                                heading="Customers / Customer Detail"
                            />

                            {overviewLoading && !overview && (
                                <div className="loader-overlay text-white">
                                    <Loader />
                                </div>
                            )}

                            <div className="d-flex align-items-center mb-3">
                                <button
                                    className="btn btn-outline-secondary btn-sm me-3"
                                    onClick={() => navigate("/customers/customerslist")}
                                >
                                    ← Back to Customers
                                </button>
                                {overview && (
                                    <h4 className="mb-0">{overview.organization.name}</h4>
                                )}
                            </div>

                            {overview && (
                                <div className="row g-2 mb-3">
                                    <div className="col-md-3">
                                        <div className="card">
                                            <div className="card-body p-3">
                                                <div className="text-muted small">Active Subs</div>
                                                <div className="fs-4 fw-bold">
                                                    {overview.active_subscription_count}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card">
                                            <div className="card-body p-3">
                                                <div className="text-muted small">Lifetime Spend</div>
                                                <div className="fs-4 fw-bold">
                                                    {fmtMoney(overview.total_lifetime_spend)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card">
                                            <div className="card-body p-3">
                                                <div className="text-muted small">Cards on File</div>
                                                <div className="fs-4 fw-bold">
                                                    {overview.payment_methods_count}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card">
                                            <div className="card-body p-3">
                                                <div className="text-muted small">Members</div>
                                                <div className="fs-4 fw-bold">
                                                    {overview.members_count}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card">
                                <div className="card-header bg-white p-0 border-bottom">
                                    <ul className="nav nav-tabs" role="tablist">
                                        {TABS.map((t) => (
                                            <li key={t.key} className="nav-item">
                                                <button
                                                    type="button"
                                                    className={`nav-link ${activeTab === t.key ? "active" : ""}`}
                                                    onClick={() => handleTabChange(t.key)}
                                                >
                                                    {t.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="card-body">
                                    {activeTab === "overview" && renderOverview()}
                                    {activeTab === "subscriptions" && renderTable(
                                        tabData.subscriptions, subsColumns,
                                        "This customer has no subscriptions."
                                    )}
                                    {activeTab === "invoices" && renderTable(
                                        tabData.invoices, invoicesColumns,
                                        "No invoices yet."
                                    )}
                                    {activeTab === "transactions" && renderTable(
                                        tabData.transactions, txnColumns,
                                        "No payment attempts recorded."
                                    )}
                                    {activeTab === "payment_methods" && renderPaymentMethods()}
                                    {activeTab === "activity" && renderTable(
                                        tabData.activity, activityColumns,
                                        "No activity recorded yet."
                                    )}
                                    {activeTab === "coupons" && renderTable(
                                        tabData.coupons, couponsColumns,
                                        "This customer has never redeemed a coupon."
                                    )}
                                    {activeTab === "trials" && renderTrials()}
                                    {activeTab === "phone_numbers" && (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div className="text-muted small">
                                                    Numbers this customer bought, had ported in, or
                                                    was given. Assigning is for numbers they cannot
                                                    buy themselves — a price of $0 means free and
                                                    sets up no recurring charge.
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-primary flex-shrink-0 ms-3"
                                                    onClick={() => setAssignOpen(true)}
                                                >
                                                    Assign a number
                                                </button>
                                            </div>
                                            {renderTable(
                                                tabData.phone_numbers, phoneNumbersColumns,
                                                "This customer has no phone numbers."
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {/* Transaction detail drawer / modal */}
            {txnDetail && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={() => setTxnDetail(null)}
                >
                    <div
                        className="modal-dialog modal-lg modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Transaction Detail</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setTxnDetail(null)}
                                />
                            </div>
                            <div className="modal-body">
                                <dl className="row">
                                    <dt className="col-sm-4">When</dt>
                                    <dd className="col-sm-8">{fmtDateTime(txnDetail.attempted_at)}</dd>
                                    <dt className="col-sm-4">Amount</dt>
                                    <dd className="col-sm-8">
                                        {fmtMoney(txnDetail.amount, txnDetail.currency || "USD")}
                                    </dd>
                                    <dt className="col-sm-4">Status</dt>
                                    <dd className="col-sm-8"><StatusPill status={txnDetail.status} /></dd>
                                    <dt className="col-sm-4">Attempt #</dt>
                                    <dd className="col-sm-8">{txnDetail.attempt_number}</dd>
                                    <dt className="col-sm-4">Auth.net Txn ID</dt>
                                    <dd className="col-sm-8" style={{ fontFamily: "monospace" }}>
                                        {txnDetail.gateway_transaction_id || "—"}
                                    </dd>
                                    <dt className="col-sm-4">Response Code</dt>
                                    <dd className="col-sm-8">
                                        {txnDetail.gateway_response_code || "—"}
                                    </dd>
                                    <dt className="col-sm-4">Response Message</dt>
                                    <dd className="col-sm-8">
                                        {txnDetail.gateway_response_message || "—"}
                                    </dd>
                                    {txnDetail.failure_reason && (
                                        <>
                                            <dt className="col-sm-4">Failure Reason</dt>
                                            <dd className="col-sm-8">{txnDetail.failure_reason}</dd>
                                        </>
                                    )}
                                    {txnDetail.failure_message && (
                                        <>
                                            <dt className="col-sm-4">Failure Message</dt>
                                            <dd className="col-sm-8">{txnDetail.failure_message}</dd>
                                        </>
                                    )}
                                    <dt className="col-sm-4">Invoice #</dt>
                                    <dd className="col-sm-8">{txnDetail.invoice_number || "—"}</dd>
                                </dl>
                                {txnDetail.gateway_response && Object.keys(txnDetail.gateway_response).length > 0 && (
                                    <>
                                        <h6 className="mt-3">Gateway Response (raw)</h6>
                                        <pre
                                            className="bg-light p-2 small"
                                            style={{ maxHeight: 200, overflow: "auto" }}
                                        >
                                            {JSON.stringify(txnDetail.gateway_response, null, 2)}
                                        </pre>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {(assignOpen || assignPrefill) && (
                <AssignPhoneNumberDialog
                    orgId={orgId}
                    orgName={overview?.organization?.name || ""}
                    prefill={assignPrefill}
                    onClose={() => { setAssignOpen(false); setAssignPrefill(null); }}
                    onAssigned={() => {
                        setAssignOpen(false);
                        setAssignPrefill(null);
                        setTabData((s) => ({ ...s, phone_numbers: null }));
                        loadTab("phone_numbers");
                    }}
                />
            )}
        </>
    );
}

export default CustomerDetail;
